<script setup lang="ts">
    import SettingsPageLayout from '../components/settings/SettingsPageLayout.vue';
    import { __ } from '../stores/expressions';
    import EchoButton from '../components/EchoButton.vue';
    import { useRouter, useRoute } from 'vue-router';
    import RoleDetails from '../components/roles/RoleDetails.vue';
    import { computed, ref, type Ref } from 'vue';
    import { useSuccessDialog } from '../stores/success-dialog';
    import { useToastStore } from '../stores/snackbar';
    import {
        useQueryGetRole,
        useMutationUpdateRole,
        useMutationUpdateRolePermissions,
    } from '@/js/services/api/role/roleQueries';
    import type { RequestCreateRole } from '@/js/services/api/role/types';
    import axios from 'axios';
    import { CheckIcon, ChevronLeftIcon, XIcon } from '@lucide/vue';

    const router = useRouter();
    const route = useRoute();

    const roleId = route.params.id as string;
    const { data: roleData } = useQueryGetRole(roleId);
    const { mutateAsync: updateRoleMutation, isPending: updateRoleIsPending } =
        useMutationUpdateRole();
    const { mutateAsync: updatePermissionsMutation, isPending: updateRolePermissionsIsPending } =
        useMutationUpdateRolePermissions();
    const role = computed(() => roleData.value ?? null);

    const successDialogStore = useSuccessDialog();
    const toastStore = useToastStore();

    type RoleDetailsRef = {
        submit: () => void;
    };

    const roleDetailsRef: Ref<RoleDetailsRef | null> = ref(null);

    const returnToRoles = () => {
        router.push({
            path: '/settings/manage-roles',
            query: route.query,
        });
    };

    const saveRole = async (
        formData: RequestCreateRole & {
            rolePermissions: Record<string, number>;
            permissionsChanged: boolean;
        }
    ) => {
        if (!role.value) return;

        const { rolePermissions, permissionsChanged, ...roleData } = formData;

        try {
            await updateRoleMutation({ roleId: role.value.id, ...roleData });

            if (permissionsChanged) {
                await updatePermissionsMutation({ roleId: role.value.id, rolePermissions });
            }
        } catch (error) {
            if (axios.isAxiosError(error) && error.response?.status === 409) {
                toastStore.addToast('error', __('error-role-name-already-exists'));
            } else {
                toastStore.addToast('error', __('error-update-role'));
            }
            return;
        }

        await successDialogStore.openDialog({
            message: __('update-role-success-message', {
                name: formData.name,
            }),
        });
    };

    const submitForm = () => {
        roleDetailsRef.value?.submit();
    };

    const savingRoleIsLoading = computed(() => {
        return updateRoleIsPending.value || updateRolePermissionsIsPending.value;
    });
</script>
<template>
    <SettingsPageLayout>
        <template #header>
            <h1 class="heading-lg-light">{{ __('edit-role') }}</h1>
        </template>
        <template #topbar>
            <EchoButton variant="primary" :loading="savingRoleIsLoading" @click="submitForm">
                {{ __('save') }}
                <template #icon-right><CheckIcon /></template>
            </EchoButton>
            <EchoButton variant="secondary" @click="returnToRoles()">
                {{ __('cancel') }}
                <template #icon-right><XIcon /></template>
            </EchoButton>
        </template>
        <template #content>
            <EchoButton
                variant="tertiary"
                :route="{ path: '/settings/manage-roles', query: route.query }"
            >
                <template #icon-left><ChevronLeftIcon /></template>
                {{ __('back-to-list') }}
            </EchoButton>

            <RoleDetails
                v-if="role"
                ref="roleDetailsRef"
                :role="role"
                :role-id="roleId"
                @save="saveRole"
            />
        </template>
    </SettingsPageLayout>
</template>
